import { useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useFilters } from '@/contexts/FilterContext';
import type { SavedSearch } from '@/lib/api/savedSearches';
import { ArticleSource, getDisplaySourceName } from '@/schema';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // When undefined, the dialog shows but actions are disabled
  activeSavedSearch?: SavedSearch;
  symbolValues: string[];
};

export function EditSavedSearchDialog({
  open,
  onOpenChange,
  activeSavedSearch,
  symbolValues,
}: Props) {
  const {
    savedSearches,
    savedSearchActions,
    selectedSymbols,
    selectedSources,
    setSelectedSymbols,
    setSelectedSources,
    hasSymbolOnly,
    setHasSymbolOnly,
  } = useFilters();

  const [editSearchName, setEditSearchName] = useState('');
  const [editSymbols, setEditSymbols] = useState<string[]>([]);
  const [editSources, setEditSources] = useState<ArticleSource[]>([]);
  const [editHasSymbolOnly, setEditHasSymbolOnly] = useState(false);
  const [editError, setEditError] = useState('');
  const [symbolQuery, setSymbolQuery] = useState('');

  const haveSameElements = (a: string[], b: string[]) => {
    if (a.length !== b.length) {
      return false;
    }
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((value, index) => value === sortedB[index]);
  };

  useEffect(() => {
    if (open && activeSavedSearch) {
      setEditSearchName(activeSavedSearch.name);
      setEditSymbols([...activeSavedSearch.symbols]);
      setEditSources([...activeSavedSearch.sources]);
      setEditHasSymbolOnly(Boolean(activeSavedSearch.hasSymbolOnly));
      setEditError('');
      setSymbolQuery('');
    }
    if (!open) {
      setEditError('');
      setSymbolQuery('');
      setEditHasSymbolOnly(false);
    }
  }, [open, activeSavedSearch]);

  const predefinedSources = useMemo(
    () => Object.values(ArticleSource) as ArticleSource[],
    []
  );

  const editHasFilters =
    editSymbols.length > 0 || editSources.length > 0 || editHasSymbolOnly;

  const savedSearchNameExists = (name: string, excludeId?: number) =>
    savedSearches.some(
      (search) =>
        search.name.toLowerCase() === name.toLowerCase() &&
        search.id !== excludeId
    );

  const toggleEditSymbol = (symbol: string) => {
    setEditSymbols((prev) =>
      prev.includes(symbol)
        ? prev.filter((existing) => existing !== symbol)
        : [...prev, symbol]
    );
  };

  const toggleEditSource = (source: ArticleSource) => {
    setEditSources((prev) =>
      prev.includes(source)
        ? prev.filter((existing) => existing !== source)
        : [...prev, source]
    );
  };

  const filteredSymbols = useMemo(() => {
    const q = symbolQuery.trim().toLowerCase();
    if (!q) return symbolValues;
    return symbolValues.filter((s) => s.toLowerCase().includes(q));
  }, [symbolValues, symbolQuery]);

  const handleEditSave = async () => {
    if (!activeSavedSearch) {
      return;
    }

    const trimmedName = editSearchName.trim();

    if (!editHasFilters) {
      setEditError(
        'Selectează cel puțin una dintre surse, simboluri sau filtrul „Doar cu simbol detectat” înainte de a salva.'
      );
      return;
    }

    if (!trimmedName) {
      setEditError('Introdu un nume pentru căutare.');
      return;
    }

    if (savedSearchNameExists(trimmedName, activeSavedSearch.id)) {
      setEditError('Există deja o altă căutare cu acest nume.');
      return;
    }

    setEditError('');

    try {
      const updatedSearch = await savedSearchActions.update({
        id: activeSavedSearch.id,
        name: trimmedName,
        filters: {
          symbols: editSymbols,
          sources: editSources,
          hasSymbolOnly: editHasSymbolOnly,
        },
      });

      // Reflect new filters in current selection only if changed
      const sameSymbols = haveSameElements(
        selectedSymbols,
        updatedSearch.symbols
      );
      const sameSources = haveSameElements(
        selectedSources,
        updatedSearch.sources
      );
      const sameHasSymbol =
        Boolean(hasSymbolOnly) === Boolean(updatedSearch.hasSymbolOnly);

      if (!sameSymbols) {
        setSelectedSymbols([...updatedSearch.symbols]);
      }
      if (!sameSources) {
        setSelectedSources([...updatedSearch.sources]);
      }
      if (!sameHasSymbol) {
        setHasSymbolOnly(Boolean(updatedSearch.hasSymbolOnly));
      }
      onOpenChange(false);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'A apărut o eroare la actualizarea căutării.';
      setEditError(message);
    }
  };

  const handleDeleteSavedSearch = async () => {
    if (!activeSavedSearch) {
      return;
    }

    setEditError('');

    try {
      await savedSearchActions.remove(activeSavedSearch.id);
      onOpenChange(false);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'A apărut o eroare la ștergerea căutării.';
      setEditError(message);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) {
          setEditError('');
        }
      }}>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editează căutarea salvată</DialogTitle>
          <DialogDescription>
            Actualizează numele și filtrele pentru această căutare salvată.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <Label htmlFor="edit-search-name">Nume căutare</Label>
          <Input
            id="edit-search-name"
            value={editSearchName}
            onChange={(event) => setEditSearchName(event.target.value)}
            placeholder="Ex: Știri ZF + BVB"
          />
          <div className="grid gap-4 rounded-md border border-neutral-200 p-4">
            <div>
              <p className="mb-2 text-sm font-medium text-neutral-700">
                Surse incluse
              </p>
              <div className="flex flex-wrap gap-2">
                {predefinedSources.map((source) => {
                  const label = getDisplaySourceName(source);
                  const checked = editSources.includes(source);

                  return (
                    <Button
                      key={source}
                      type="button"
                      variant={checked ? 'default' : 'outline'}
                      onClick={() => toggleEditSource(source)}>
                      {label}
                    </Button>
                  );
                })}
              </div>
            </div>
            <div>
              <p className="mb-2 text-sm font-medium text-neutral-700">
                Simboluri incluse
              </p>
              <div className="mb-2">
                <Input
                  value={symbolQuery}
                  onChange={(e) => setSymbolQuery(e.target.value)}
                  placeholder="Caută simbol..."
                />
              </div>
              <div className="flex max-h-48 flex-wrap gap-2 overflow-y-auto rounded-md border border-neutral-200 p-2">
                {symbolValues.length === 0 ? (
                  <p className="text-sm text-neutral-500">
                    Niciun simbol disponibil pentru moment.
                  </p>
                ) : filteredSymbols.length === 0 ? (
                  <p className="text-sm text-neutral-500">
                    Niciun simbol corespunde căutării.
                  </p>
                ) : (
                  filteredSymbols.map((symbol) => {
                    const checked = editSymbols.includes(symbol);
                    return (
                      <Button
                        key={symbol}
                        type="button"
                        variant={checked ? 'default' : 'outline'}
                        onClick={() => toggleEditSymbol(symbol)}>
                        {symbol}
                      </Button>
                    );
                  })
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="edit-has-symbol-only"
                checked={editHasSymbolOnly}
                onCheckedChange={(checked) =>
                  setEditHasSymbolOnly(Boolean(checked))
                }
              />
              <Label
                htmlFor="edit-has-symbol-only"
                className="text-sm text-neutral-700">
                Doar cu simbol detectat
              </Label>
            </div>
          </div>
          {editError && <p className="text-sm text-destructive">{editError}</p>}
        </div>
        <DialogFooter>
          {activeSavedSearch && (
            <Button
              variant="destructive"
              onClick={handleDeleteSavedSearch}
              className="mr-auto">
              Șterge căutarea
            </Button>
          )}
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Renunță
          </Button>
          <Button onClick={handleEditSave} disabled={!editHasFilters}>
            Salvează modificările
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default EditSavedSearchDialog;
